/**
 * F5 — "Finished, not invoiced": jobs a tech marked done that nobody has billed.
 *
 * The trap this exists for is a job finished in the field and never invoiced,
 * found months later or never. It sits at the top of the office's landing
 * screen so that it is seen daily, oldest first as the server orders it, and a
 * job waiting more than a week is flagged.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { waitingLabel, waitingTooLong } from "../lib/daywork";
import { money } from "../lib/format";
import { Card, Chip } from "./ui";

export function ToInvoiceCard() {
  const { can } = useAuth();
  const office = can(...OFFICE);
  const list = useQuery({
    // Under "invoices" so raising an invoice (which invalidates it) refreshes this.
    queryKey: ["invoices", "to-raise"],
    queryFn: () => api.invoices.toRaise(),
    enabled: office,
  });

  if (!office) return null;

  const rows = list.data ?? [];

  return (
    <Card
      title="Finished, not invoiced"
      subtitle={list.data ? `${rows.length} job${rows.length === 1 ? "" : "s"}` : undefined}
      padded={false}
    >
      {list.isPending ? (
        <p className="tiny muted toraise-note">Checking for finished jobs…</p>
      ) : list.error ? (
        <p className="tiny muted toraise-note">
          Couldn't check for finished jobs.{" "}
          <button type="button" className="linkish tiny toraise-retry" onClick={() => void list.refetch()}>
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="tiny muted toraise-note">Nothing finished is waiting to be invoiced.</p>
      ) : (
        <ul className="toraise-list">
          {rows.map((row) => (
            <li key={row.job_id} className="toraise-row">
              <div className="toraise-main">
                <Link to={`/jobs/${row.job_id}`} className="toraise-link mono">
                  {row.job_number}
                </Link>
                <span className="toraise-title">{row.title}</span>
                <span className="tiny muted">{row.customer_name}</span>
                {row.blocked && <span className="tiny toraise-blocked">{row.blocked}</span>}
              </div>
              <div className="toraise-side">
                {waitingTooLong(row.days_waiting) ? (
                  <Chip tone="warn">{waitingLabel(row.days_waiting)}</Chip>
                ) : (
                  <span className="tiny muted">{waitingLabel(row.days_waiting)}</span>
                )}
                <span className="mono">
                  {row.draft_subtotal_cents === null ? "—" : money(row.draft_subtotal_cents)}
                </span>
                <span className="tiny muted">ex GST</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
